import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { HandleImageErrorDirective } from '../../../directives/handle-image-error.directive';
import { AiChatProductCardModel, AiChatProductCardsService } from '../../../services/ai-chat-product-cards.service';
import { pairEntries } from '../../../utils/ai-chat-product-cards';

/**
 * One product's entry in a Pebbl reply: its picture on the left, the entry's own reply text -
 * projected, so it keeps the chat surface's Markdown styling - on the right. Shared by the widget
 * and /ask-pebbl.
 *
 * The picture is decorative beside a name that is already a link, so it is hidden from assistive
 * tech and out of the tab order. A placeholder holds its final size while the SKU resolves, so
 * the reply does not jump; a SKU that does not resolve leaves the entry as text alone. The SKU
 * itself is never rendered: not as text, alt, title or aria-label.
 */
@Component({
  selector: 'app-ai-product-entry',
  standalone: true,
  imports: [HandleImageErrorDirective],
  templateUrl: './ai-product-entry.component.html',
  styleUrl: './ai-product-entry.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.has-picture]': 'state() !== "missing"'
  }
})
export class AiProductEntryComponent implements OnChanges {
  /* ################### Variable Declaration Start ################### */
  /** The reply's whole SKU list - every entry asks with it, so they share one lookup. */
  @Input({ required: true }) skus: string[] = [];
  /** This entry's number in the reply. */
  @Input({ required: true }) index = 0;
  /** Every entry's product link, by entry number - the picture is the card this entry links to. */
  @Input() hrefs: (string | null)[] = [];
  /** Each SKU's `product_link`, index for index with `skus` - what the entry's link must equal. */
  @Input() links: (string | null)[] = [];

  card = signal<AiChatProductCardModel | null>(null);
  state = signal<'loading' | 'ready' | 'missing'>('loading');

  /** The SKU list, index, hrefs and links last resolved - a re-render per token must not fetch again. */
  private resolvedKey: string | null = null;
  private subscription?: Subscription;
  private readonly destroyRef = inject(DestroyRef);
  /* ################### Variable Declaration End ################### */

  constructor(private productCardsService: AiChatProductCardsService) { }

  ngOnChanges(): void {
    const skus = this.skus ?? [];
    const hrefs = this.hrefs ?? [];
    const links = this.links ?? [];
    const key = JSON.stringify([this.index, skus, hrefs, links]);
    if (key === this.resolvedKey) {
      return;
    }
    this.resolvedKey = key;

    this.subscription?.unsubscribe();
    if (!skus.length) {
      this.card.set(null);
      this.state.set('missing');
      return;
    }

    this.state.set('loading');
    this.subscription = this.productCardsService.resolveEach(skus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cards) => {
        const match = pairEntries(hrefs, cards, links)[this.index] ?? null;
        const card = match === null ? null : cards[match];
        this.card.set(card);
        this.state.set(card ? 'ready' : 'missing');
      });
  }
}
